"use client"

import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import { useIsDesktop } from "ui"
import ButtonWithArrow, {
  colorPalletteIndexType,
} from "ui/common/bit/ButtonWithArrow"
import { mediaQueries } from "ui/theme/mediaQueries"
import { colors, sizes } from "ui/theme/theme"
import { getNonUmbracoUrl } from "utils"

const StyledContactBox = styled(Box)<{ delay?: number }>`
  display: flex;
  align-items: space-between;
  justify-content: center;
  background-color: ${colors.white};
  border-radius: ${sizes.baseDouble};
  box-shadow: -5.759px 11.517px 43.189px 0px rgba(2, 1, 64, 0.12);
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  ${({ delay }) => delay && `transition-delay: ${delay}ms;`}
  height: 100%;
  gap: ${sizes.baseDouble};
  &.in-view {
    opacity: 1;
    transform: translateY(0);
  }
  ${mediaQueries.mobile} {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    padding: 14px;
    align-items: center;
  }
  ${mediaQueries.tablet} {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    padding: 14px;
    align-items: center;
  }
  ${mediaQueries.desktopM} {
    padding: 28px 5px;
    width: 185px;
    height: 212px;
    flex-direction: column;
    opacity: 1; /* Always visible on desktop */
    transform: translateY(0); /* No animation on desktop */
    transition: none;
  }
  ${mediaQueries.desktopXl} {
    padding: 24px ${sizes.baseDouble};
    width: 233px;
    height: 215px;
  }
`

const StyledIconWrapper = styled(Box)<{ bgcolor?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${sizes.baseOcto};
  height: ${sizes.baseOcto};
  background-color: ${({ bgcolor }) => bgcolor && `#${bgcolor}`};
  border-radius: ${sizes.baseSingle};
  flex: 0 0 auto;
  img {
    object-fit: contain;
  }
  ${mediaQueries.mobile} {
    width: ${sizes.basePenta};
    height: ${sizes.basePenta};
  }
`

const StyledBodyText = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.black};
  flex-grow: 1;
  ${mediaQueries.mobile} {
    font-size: 16px;
  }
`

const StyledButtonWithArrow = styled(ButtonWithArrow)`
  background-color: ${colors.white};
  font-size: 15px;
  padding: 6px 14px;
  ${mediaQueries.mobile} {
    height: 44px;
    svg {
      width: auto;
    }
  }
  ${mediaQueries.desktopM} {
    margin-top: auto;
    flex-shrink: 0;
    justify-content: space-between;
    padding: 6px 14px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 16px;
  }
`

interface ContactBoxDataProps {
  icon?: string
  title: string
  url?: { url: string; name: string }
  primaryColor?: string
  delay?: number
  isInView?: boolean
  className?: string
}

interface ContactBoxProps {
  data: ContactBoxDataProps
  onClick?: (buttonName: string) => void
}

const ContactBox = ({ data, onClick }: ContactBoxProps) => {
  const {
    icon,
    title,
    url,
    primaryColor,
    delay,
    isInView = true,
    className = "",
  } = data
  const isDesktop = useIsDesktop()
  const arrowName = primaryColor
    ? (colors.resolveColorName("#" + primaryColor) as
        | colorPalletteIndexType
        | undefined)
    : undefined

  const combinedClassName = `${className} ${!isDesktop && isInView ? "in-view" : ""}`

  return (
    <StyledContactBox delay={delay} className={combinedClassName}>
      {icon && (
        <StyledIconWrapper bgcolor={primaryColor}>
          <Image
            width={isDesktop ? 35 : 25}
            height={isDesktop ? 35 : 25}
            src={icon}
            alt={title}
          />
        </StyledIconWrapper>
      )}
      <StyledBodyText variant="body2">{title}</StyledBodyText>
      {url && (
        <StyledButtonWithArrow
          href={getNonUmbracoUrl(url.url)}
          colorPallette={arrowName}
          variant="elevated"
          data-dev-only="true"
          ArrowWithLine={isDesktop}
          onClick={onClick ? () => onClick(url.name) : undefined}
        >
          <Typography variant="CTA">{url.name}</Typography>
        </StyledButtonWithArrow>
      )}
    </StyledContactBox>
  )
}

export default ContactBox
